import { Pencil, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import type { Language, ReportSection } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { LoadingSpinner } from "~/components/Icons";
import { Button } from "~/components/ui/button";
import {
  BatesLink,
  SectionLink,
  TERMS_PROSE_CLASS,
  getTermsAndSectionsMarkdownComponents,
  highlightTermsAndSectionsInMarkdown,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { EditReportSectionDialog } from "./EditReportSectionDialog";
import { localizeReportSectionTitle } from "./localizeReportSectionTitle";
import {
  ReportSectionSources,
  ReportSectionWithSources,
  SourceReference,
} from "./ReportSectionSources";
import { ReportViewStatus } from "./ReportView";

export const ReportSectionView = ({
  reportSection,
  terms,
  sectionLinks = [],
  onRegenerate,
  onEdit,
  isRegenerating,
  status,
  templateName,
  outputLanguage,
  isActionAvailable = true,
  onSourceClick,
  onSectionClick,
  batesLinks,
  onBatesClick,
}: {
  reportSection: ReportSection;
  terms: Hint[];
  sectionLinks?: SectionLink[];
  onRegenerate: () => void;
  onEdit: (content: string) => Promise<void>;
  isRegenerating: boolean;
  status: ReportViewStatus;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionAvailable?: boolean;
  onSourceClick?: (source: SourceReference) => void;
  onSectionClick?: (sectionId: string, firstPageId: string) => void;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
}) => {
  // Cast to extended type to access sources during transition period
  const sectionWithSources = reportSection as ReportSectionWithSources;
  const sources = (sectionWithSources.sources ?? []) as SourceReference[];
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const displayTitle = localizeReportSectionTitle(
    reportSection.title,
    templateName,
    outputLanguage
  );

  const highlightedContent = useMemo(() => {
    if (!reportSection.content) return "";
    const preprocessed = unwrapAllCodeBlocks(reportSection.content);
    return highlightTermsAndSectionsInMarkdown(
      preprocessed,
      terms,
      sectionLinks,
      batesLinks
    );
  }, [reportSection.content, terms, sectionLinks, batesLinks]);

  return (
    <div className="group">
      <h2 className="text-sm font-semibold mb-2">{displayTitle}</h2>
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        className={TERMS_PROSE_CLASS}
        components={{
          ...getTermsAndSectionsMarkdownComponents(
            terms,
            sectionLinks,
            onSectionClick,
            onBatesClick
          ),
        }}
      >
        {highlightedContent}
      </Markdown>
      {sources.length > 0 && (
        <ReportSectionSources sources={sources} onSourceClick={onSourceClick} />
      )}
      <div className="h-4 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
        <div className="inline-flex items-center gap-2">
          <ContentCopyButton
            content={reportSection.content ?? ""}
            preCopyMessageId="copy"
            color="muted-foreground"
          />
          {isActionAvailable && (
            <Button
              variant="ghost"
              size="mini"
              className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
              onClick={() => setEditDialogOpen(true)}
              disabled={isRegenerating}
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
          {isActionAvailable && (
            <Button
              variant="ghost"
              size="mini"
              className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
              onClick={onRegenerate}
              disabled={
                isRegenerating ||
                status.type === "outdated" ||
                status.type === "templateDeleted" ||
                status.type === "pending"
              }
            >
              {isRegenerating ? (
                <LoadingSpinner className="w-4 h-4 mr-1" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              Rewrite
            </Button>
          )}
        </div>
      </div>

      <EditReportSectionDialog
        section={reportSection}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={onEdit}
      />
    </div>
  );
};
